import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

const BUCKET = 'job-evidence'
const MAX_FILE_SIZE = 100 * 1024 * 1024

const STAGES = [
  ['intake', 'Intake'],
  ['inspection', 'Inspection'],
  ['service', 'Service'],
  ['qc', 'QC'],
  ['handover', 'Handover'],
  ['other', 'Other'],
]

const EVIDENCE_TYPES = [
  ['photo', 'Photo'],
  ['video', 'Video'],
  ['file', 'File / Document'],
  ['dyno_testing_report', 'Dyno Testing Report'],
]

function evidenceTypeLabel(value, context = 'suspension') {
  if (context === 'general' && value === 'dyno_testing_report') return 'Workshop Test Report'
  return EVIDENCE_TYPES.find(([key]) => key === value)?.[1] || value || 'File / Document'
}

function evidenceType(file, selectedType = 'auto') {
  if (selectedType && selectedType !== 'auto') return selectedType
  if (String(file.type || '').startsWith('image/')) return 'photo'
  if (String(file.type || '').startsWith('video/')) return 'video'
  return 'file'
}

function safeFileName(name = 'upload') {
  const cleaned = String(name)
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return cleaned.slice(0, 180) || 'upload'
}

function bytesLabel(bytes) {
  const value = Number(bytes || 0)
  if (!value) return ''
  if (value > 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`
  if (value > 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${value} B`
}

export default function JobEvidence({
  jobId,
  workshopId,
  mode = 'edit',
  onChanged,
  onLoaded,
  context = 'suspension',
  checklistItemId = null,
  defaultStage = '',
  lockStage = false,
  compact = false,
}) {
  const canEdit = mode === 'edit'
  const isGeneral = context === 'general'
  const evidenceTypeOptions = isGeneral
    ? EVIDENCE_TYPES.filter(([value]) => value !== 'dyno_testing_report')
    : EVIDENCE_TYPES
  const inputRef = useRef(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraStarting, setCameraStarting] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [error, setError] = useState('')
  const [caption, setCaption] = useState('')
  const [stage, setStage] = useState(defaultStage || 'service')
  const [visibility, setVisibility] = useState('internal')
  const [selectedType, setSelectedType] = useState('auto')

  useEffect(() => () => stopCamera(), [])

  useEffect(() => {
    if (defaultStage) setStage(defaultStage)
  }, [defaultStage])

  useEffect(() => {
    let cancelled = false

    async function loadEvidence() {
      if (!jobId) {
        setItems([])
        return
      }
      setLoading(true)
      setError('')
      let query = supabase
        .from('job_evidence')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false })
      if (checklistItemId) query = query.eq('checklist_item_id', checklistItemId)
      const { data, error: loadError } = await query

      if (cancelled) return
      if (loadError) {
        setError(loadError.message)
        setItems([])
        setLoading(false)
        return
      }

      const withUrls = await Promise.all((data || []).map(async item => {
        const { data: signed } = await supabase.storage
          .from(item.storage_bucket || BUCKET)
          .createSignedUrl(item.storage_path, 60 * 60)
        return { ...item, signed_url: signed?.signedUrl || '' }
      }))

      if (!cancelled) {
        setItems(withUrls)
        if (onLoaded) onLoaded(withUrls)
        setLoading(false)
      }
    }

    loadEvidence()
    return () => { cancelled = true }
  }, [jobId, checklistItemId])

  async function refresh() {
    if (!jobId) return
    let query = supabase
      .from('job_evidence')
      .select('*')
      .eq('job_id', jobId)
      .order('created_at', { ascending: false })
    if (checklistItemId) query = query.eq('checklist_item_id', checklistItemId)
    const { data } = await query

    const withUrls = await Promise.all((data || []).map(async item => {
      const { data: signed } = await supabase.storage
        .from(item.storage_bucket || BUCKET)
        .createSignedUrl(item.storage_path, 60 * 60)
      return { ...item, signed_url: signed?.signedUrl || '' }
    }))
    setItems(withUrls)
    if (onLoaded) onLoaded(withUrls)
  }

  async function saveFiles(files, { clearFileInput = false } = {}) {
    if (!files.length || !jobId || !workshopId) return

    setUploading(true)
    setError('')
    try {
      const { data: userData } = await supabase.auth.getUser()
      const uploadedBy = userData?.user?.id || null

      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          throw new Error(`${file.name} is over the 100 MB upload limit.`)
        }

        const unique = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`
        const path = `${workshopId}/${jobId}/${unique}-${safeFileName(file.name)}`

        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
        if (uploadError) throw uploadError

        const { error: insertError } = await supabase.from('job_evidence').insert({
          workshop_id: workshopId,
          job_id: jobId,
          ...(checklistItemId ? { checklist_item_id: checklistItemId } : {}),
          uploaded_by: uploadedBy,
          storage_bucket: BUCKET,
          storage_path: path,
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          file_size: file.size,
          evidence_type: evidenceType(file, selectedType),
          visibility,
          stage,
          caption: caption.trim() || null,
        })
        if (insertError) throw insertError
      }

      setCaption('')
      if (clearFileInput && inputRef.current) inputRef.current.value = ''
      await refresh()
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  async function uploadFiles(event) {
    const files = Array.from(event.target.files || [])
    await saveFiles(files, { clearFileInput: true })
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) videoRef.current.srcObject = null
    setCameraOpen(false)
    setCameraStarting(false)
  }

  async function openCamera() {
    setCameraError('')
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera capture is not available in this browser.')
      return
    }

    setCameraOpen(true)
    setCameraStarting(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1600 },
          height: { ideal: 1200 },
        },
      })
      streamRef.current = stream
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current
          videoRef.current.play().catch(() => {})
        }
      }, 0)
    } catch (err) {
      stopCamera()
      setCameraError(err?.name === 'NotAllowedError'
        ? 'Camera permission was blocked. Allow camera access in the browser to take photos.'
        : 'Camera could not start on this device.')
    } finally {
      setCameraStarting(false)
    }
  }

  async function capturePhoto() {
    setCameraError('')
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || !video.videoWidth) {
      setCameraError('Camera is not ready yet.')
      return
    }

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob) {
      setCameraError('Could not capture the photo.')
      return
    }

    const file = new File([blob], `job-photo-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`, {
      type: 'image/jpeg',
    })
    await saveFiles([file])
  }

  async function updateItem(item, patch) {
    setItems(rows => rows.map(row => row.id === item.id ? { ...row, ...patch } : row))
    const { error: updateError } = await supabase
      .from('job_evidence')
      .update(patch)
      .eq('id', item.id)

    if (updateError) {
      setError(updateError.message)
      await refresh()
      return
    }
    await refresh()
    if (onChanged) onChanged()
  }

  async function deleteItem(item) {
    const { error: storageError } = await supabase.storage
      .from(item.storage_bucket || BUCKET)
      .remove([item.storage_path])
    if (storageError) {
      setError(storageError.message)
      return
    }

    const { error: deleteError } = await supabase
      .from('job_evidence')
      .delete()
      .eq('id', item.id)

    if (deleteError) {
      setError(deleteError.message)
      return
    }

    const next = items.filter(row => row.id !== item.id)
    setItems(next)
    if (onLoaded) onLoaded(next)
    if (onChanged) onChanged()
  }

  function renderPreview(item) {
    if (!item.signed_url) return <div className="text-muted fs12">Preview unavailable</div>
    if (item.evidence_type === 'photo') {
      return (
        <a href={item.signed_url} target="_blank" rel="noopener noreferrer">
          <img src={item.signed_url} alt={item.caption || item.file_name} style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
        </a>
      )
    }
    if (item.evidence_type === 'video') {
      return <video src={item.signed_url} controls style={{ width: 128, height: 72, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--black)' }} />
    }
    return (
      <a href={item.signed_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
        Open file
      </a>
    )
  }

  if (!jobId) {
    return <div className="fs12 text-muted">Save the job before adding photos, videos or files.</div>
  }

  return (
    <div>
      {canEdit && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: compact ? 10 : 14, background: 'var(--black)', marginBottom: 14 }}>
          <div className="grid-3">
            <div className="field">
              <label>Stage</label>
              <select value={stage} onChange={e => setStage(e.target.value)} disabled={lockStage}>
                {STAGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              {lockStage && <div className="field-hint">Linked to this checklist item.</div>}
            </div>
            <div className="field">
              <label>Visibility</label>
              <select value={visibility} onChange={e => setVisibility(e.target.value)}>
                <option value="internal">Internal only</option>
                <option value="customer">Customer report</option>
              </select>
            </div>
            <div className="field">
              <label>Type</label>
              <select value={selectedType} onChange={e => setSelectedType(e.target.value)}>
                <option value="auto">Auto detect</option>
                {evidenceTypeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Files</label>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.txt,.csv"
                onChange={uploadFiles}
                disabled={uploading}
              />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Caption / note</label>
            <input
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder={isGeneral ? 'e.g. Rear brake pads before replacement' : 'e.g. Stanchion wear before service'}
            />
          </div>
          <div className="action-row" style={{ marginTop: 10 }}>
            {!cameraOpen ? (
              <button className="btn btn-ghost btn-sm" onClick={openCamera} disabled={uploading || cameraStarting}>
                {cameraStarting ? 'Opening camera...' : 'Take Photo'}
              </button>
            ) : (
              <>
                <button className="btn btn-primary btn-sm" onClick={capturePhoto} disabled={uploading || cameraStarting}>
                  {uploading ? 'Saving...' : 'Capture Photo'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={stopCamera} disabled={cameraStarting}>
                  Close Camera
                </button>
              </>
            )}
          </div>
          {cameraError && <div className="alert alert-error" style={{ marginTop: 10, marginBottom: 0 }}>{cameraError}</div>}
          {cameraOpen && (
            <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', background: 'var(--panel)' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ display: 'block', width: '100%', maxHeight: 360, objectFit: 'contain', background: 'var(--black)' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          )}
          <div className="fs11 text-muted" style={{ marginTop: 8 }}>
            {isGeneral
              ? 'Take a photo with the device camera, or upload photos, videos and PDFs up to 100 MB. Customer-visible items can be included in General Workshop reports.'
              : 'Take a photo with the device camera, or upload photos, videos and PDFs up to 100 MB. Customer-visible items can be included in service reports.'}
          </div>
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
      {uploading && <div className="alert alert-info" style={{ marginBottom: 10 }}>Uploading evidence...</div>}
      {loading && <div className="fs12 text-muted">Loading evidence...</div>}
      {!loading && !items.length && <div className="fs12 text-muted">No photos, videos or files added yet.</div>}

      <div style={{ display: 'grid', gap: 10 }}>
        {items.map(item => (
          <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'start', border: '1px solid var(--border)', borderRadius: 6, padding: 12, background: 'var(--panel)' }}>
            <div>{renderPreview(item)}</div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div className="fw7">{item.caption || item.file_name}</div>
                  <div className="fs11 text-muted" style={{ marginTop: 3 }}>
                    {STAGES.find(([value]) => value === item.stage)?.[1] || item.stage} - {evidenceTypeLabel(item.evidence_type, context)} {bytesLabel(item.file_size) ? `- ${bytesLabel(item.file_size)}` : ''}
                  </div>
                </div>
                <span className={`job-status-chip job-status-${item.visibility === 'customer' ? 'green' : 'muted'}`}>
                  {item.visibility === 'customer' ? 'Customer report' : 'Internal only'}
                </span>
              </div>

              {canEdit ? (
                <div className="grid-3" style={{ marginTop: 10 }}>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Caption</label>
                    <input value={item.caption || ''} onChange={e => updateItem(item, { caption: e.target.value })} />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Type</label>
                    <select value={item.evidence_type || 'file'} onChange={e => updateItem(item, { evidence_type: e.target.value })}>
                      {[
                        ...evidenceTypeOptions,
                        ...(isGeneral && item.evidence_type === 'dyno_testing_report'
                          ? [['dyno_testing_report', evidenceTypeLabel('dyno_testing_report', context)]]
                          : []),
                      ].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Visibility</label>
                    <select value={item.visibility} onChange={e => updateItem(item, { visibility: e.target.value })}>
                      <option value="internal">Internal only</option>
                      <option value="customer">Customer report</option>
                    </select>
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Actions</label>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteItem(item)}>Delete</button>
                  </div>
                </div>
              ) : item.caption ? (
                <div className="fs12 text-muted" style={{ marginTop: 8 }}>{item.file_name}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
